import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';
import { useToastContext } from '../../contexts/ToastContext';

const DEFAULT_CATEGORIES = [
 { value: 'getting-started', label: 'Getting started' },
 { value: 'bookings', label: 'Bookings' },
 { value: 'payments', label: 'Payments' },
 { value: 'account', label: 'Account' },
 { value: 'safety', label: 'Safety' },
 { value: 'commission', label: 'Commission' },
 { value: 'email', label: 'Email' },
 { value: 'site-settings', label: 'Site settings' },
 { value: 'general', label: 'General' },
];

const AdminFAQModal = ({
 isOpen,
 onClose,
 faq = null,
 createFAQ,
 updateFAQ,
 isCreating = false,
 isUpdating = false,
 existingFaqs = [],
}) => {
 const { success, error: showError } = useToastContext();
 const [formData, setFormData] = useState({
 question: '',
 answer: '',
 category: 'general',
 order: 0,
 is_active: true,
 });
 const [errors, setErrors] = useState({});

 const categoryOptions = useMemo(() => {
 const fromData = (existingFaqs || [])
 .map((f) => f.category)
 .filter(Boolean)
 .filter((c, i, arr) => arr.indexOf(c) === i)
 .map((c) => ({
 value: c,
 label: c.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
 }));
 const merged = [...DEFAULT_CATEGORIES];
 fromData.forEach((opt) => {
 if (!merged.some((m) => m.value === opt.value)) merged.push(opt);
 });
 return merged;
 }, [existingFaqs]);

 useEffect(() => {
 if (!isOpen) return;
 if (faq) {
 setFormData({
 question: faq.question || '',
 answer: faq.answer || '',
 category: faq.category || 'general',
 order: faq.order ?? 0,
 is_active: faq.is_active ?? true,
 });
 } else {
 setFormData({
 question: '',
 answer: '',
 category: 'general',
 order: 0,
 is_active: true,
 });
 }
 setErrors({});
 }, [isOpen, faq]);

 const validate = () => {
 const next = {};
 if (!formData.question.trim()) next.question = 'Question is required';
 if (!formData.answer.trim()) next.answer = 'Answer is required';
 setErrors(next);
 return Object.keys(next).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!validate()) return;

 const payload = {
 question: formData.question.trim(),
 answer: formData.answer.trim(),
 category: formData.category || 'general',
 order: Number(formData.order) || 0,
 is_active: Boolean(formData.is_active),
 };

 const onSuccess = () => {
 success(faq ? 'FAQ updated successfully' : 'FAQ created successfully');
 onClose();
 };
 const onError = (err) => {
 const msg = err?.response?.data?.message || err?.message || 'Failed to save FAQ';
 showError(msg);
 if (err?.response?.data?.errors) setErrors(err.response.data.errors);
 };

 if (faq?.id) {
 updateFAQ({ id: faq.id, data: payload }, { onSuccess, onError });
 } else {
 createFAQ(payload, { onSuccess, onError });
 }
 };

 if (!isOpen) return null;

 const saving = isCreating || isUpdating;

 return (
 <div className="fixed inset-0 z-50 overflow-y-auto">
 <div className="flex items-center justify-center min-h-screen p-4">
 <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden />
 <div className="relative bg-surface rounded-lg shadow-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
 <div className="sticky top-0 bg-surface border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
 <h2 className="text-xl font-bold text-gray-900">
 {faq ? 'Edit FAQ' : 'Create FAQ'}
 </h2>
 <button
 type="button"
 onClick={onClose}
 className="text-gray-400 hover:text-gray-600"
 aria-label="Close"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="p-6 space-y-5">
 <Input
 label="Question"
 value={formData.question}
 onChange={(e) => {
 setFormData((prev) => ({ ...prev, question: e.target.value }));
 if (errors.question) setErrors((prev) => ({ ...prev, question: undefined }));
 }}
 error={errors.question}
 required
 placeholder="What is your question?"
 />

 <TextArea
 label="Answer"
 value={formData.answer}
 onChange={(e) => {
 setFormData((prev) => ({ ...prev, answer: e.target.value }));
 if (errors.answer) setErrors((prev) => ({ ...prev, answer: undefined }));
 }}
 error={errors.answer}
 rows={6}
 placeholder="Provide a clear answer..."
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <SearchSelect
 label="Category"
 options={categoryOptions}
 value={formData.category}
 onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
 searchable
 placeholder="Select category"
 />
 <Input
 label="Display order"
 type="number"
 min={0}
 value={formData.order}
 onChange={(e) =>
 setFormData((prev) => ({ ...prev, order: e.target.value }))
 }
 placeholder="0"
 />
 </div>

 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={formData.is_active}
 onChange={(e) =>
 setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
 }
 className="h-4 w-4 rounded border-gray-300 text-ink focus:ring-ink"
 />
 <span className="text-sm text-gray-700">Active (visible in help center)</span>
 </label>

 <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
 <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
 Cancel
 </Button>
 <Button type="submit" disabled={saving}>
 {saving ? 'Saving…' : faq ? 'Save changes' : 'Create FAQ'}
 </Button>
 </div>
 </form>
 </div>
 </div>
 </div>
 );
};

export default AdminFAQModal;
